import React from "react";
import * as Sentry from "@sentry/react";
import { Result, Button } from "antd";
import { logError } from "../shared/utils/logger.js";

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Log to our logger
    logError("ErrorBoundary caught an error:", error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || "Unknown",
    });

    // Capture error with Sentry
    Sentry.withScope((scope) => {
      scope.setTag("errorBoundary", true);
      scope.setTag("errorBoundaryName", this.props.name || "Unknown");
      scope.setContext("errorInfo", {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.props.name || "Unknown",
      });
      scope.setLevel("error");
      Sentry.captureException(error);
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReportError = () => {
    // Report error to Sentry with additional context
    Sentry.withScope((scope) => {
      scope.setTag("userReported", true);
      scope.setContext("userAction", {
        action: "manual_error_report",
        errorBoundary: this.props.name || "Unknown",
        timestamp: new Date().toISOString(),
      });
      Sentry.captureException(this.state.error);
    });

    // Show success message
    if (this.props.onErrorReported) {
      this.props.onErrorReported();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle={
            this.props.showDetails && this.state.error
              ? this.state.error.message
              : "An unexpected error occurred. Please try refreshing the page."
          }
          extra={[
            <Button type="primary" key="retry" onClick={this.handleRetry}>
              Try Again
            </Button>,
            <Button key="report" onClick={this.handleReportError}>
              Report Error
            </Button>,
            <Button key="refresh" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>,
          ]}
        >
          {this.props.showDetails && this.state.errorInfo && (
            <details style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
              <summary>Error Details</summary>
              <pre>{this.state.error && this.state.error.toString()}</pre>
              <pre>{this.state.errorInfo.componentStack}</pre>
            </details>
          )}
        </Result>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component for wrapping components with error boundary
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {Object} errorBoundaryProps - Props to pass to ErrorBoundary
 * @returns {React.Component} Wrapped component with error boundary
 */
export const withErrorBoundary = (
  WrappedComponent,
  errorBoundaryProps = {}
) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundary;
};

/**
 * Hook for error boundary functionality in functional components
 * @param {string} componentName - Name of the component for error tracking
 * @returns {Object} Error boundary utilities
 */
export const useErrorBoundary = (componentName = "Unknown") => {
  const [error, setError] = React.useState(null);

  const resetError = () => setError(null);

  const captureError = (error, errorInfo = {}) => {
    setError(error);

    // Log to our logger
    logError(`Error in ${componentName}:`, error, {
      componentName,
      ...errorInfo,
    });

    // Capture error with Sentry
    Sentry.withScope((scope) => {
      scope.setTag("componentName", componentName);
      scope.setTag("errorBoundary", false); // This is not from error boundary
      scope.setContext("errorInfo", {
        componentName,
        ...errorInfo,
      });
      scope.setLevel("error");
      Sentry.captureException(error);
    });
  };

  return {
    error,
    resetError,
    captureError,
  };
};

export default ErrorBoundary;
